import { TrashIcon } from '@phosphor-icons/react';
import { css, html } from 'react-strict-dom';
import { color } from 'src/constants/color.css';

import { Text } from '../../../components';

type Props = Readonly<{
  inProgress?: boolean;
  originalName: string | null;
  thumbUrl?: string;
  url?: string;
  onDelete?: () => void;
}>;

export default function UploadCard({
  inProgress,
  originalName,
  thumbUrl,
  url,
  onDelete,
}: Props) {
  const extension = originalName?.split('.').pop();

  const inner = (
    <>
      {onDelete && (
        <html.button
          type="button"
          aria-label="Delete"
          style={styles.deleteButton}
          onClick={(e) => {
            e.preventDefault();
            e.stopPropagation();
            onDelete();
          }}
        >
          <TrashIcon size={20} color={color.neutral80} />
        </html.button>
      )}
      {thumbUrl ? (
        <html.img
          src={thumbUrl}
          alt={originalName ?? ''}
          style={[styles.thumb, styles.thumbImg]}
        />
      ) : (
        <Text as="p" style={[styles.thumb, styles.thumbPlaceholder]}>
          {extension ?? ''}
        </Text>
      )}
      {originalName && (
        <Text style={[styles.title, !!url && styles.titleLink]}>
          {originalName}
        </Text>
      )}
    </>
  );

  return url ? (
    <html.a
      href={url}
      target="_blank"
      rel="noopener noreferrer"
      style={[styles.card, inProgress && styles.cardInProgress]}
    >
      {inner}
    </html.a>
  ) : (
    <html.div style={[styles.card, inProgress && styles.cardInProgress]}>
      {inner}
    </html.div>
  );
}

const styles = css.create({
  card: {
    position: 'relative',
    display: 'flex',
    flexDirection: 'column',
    width: 160,
    marginTop: 5,
    marginBottom: 5,
    paddingBottom: 8,
    boxSizing: 'border-box',
    backgroundColor: color.neutral10,
    borderRadius: 8,
    overflow: 'hidden',
    textDecoration: 'none',
    gap: 8,
  },
  cardInProgress: {
    opacity: 0.5,
  },
  thumb: {
    width: '100%',
    height: 120,
    backgroundColor: color.neutral20,
  },
  thumbImg: {
    objectFit: 'contain',
  },
  thumbPlaceholder: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    fontSize: 26,
    color: color.neutral40,
  },
  title: {
    display: 'block',
    fontSize: 14,
    overflow: 'hidden',
    whiteSpace: 'nowrap',
    textOverflow: 'ellipsis',
    paddingLeft: 8,
    paddingRight: 8,
    color: color.neutral80,
  },
  titleLink: {
    color: color.link,
    ':hover': {
      textDecoration: 'underline',
    },
  },
  deleteButton: {
    position: 'absolute',
    top: 4,
    right: 4,
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    width: 40,
    height: 40,
    padding: 0,
    backgroundColor: color.neutral0,
    borderRadius: 8,
    cursor: 'pointer',
    borderColor: color.neutral0,
  },
});
